const SESSION_KEY = "kiosk:customer-view";

export type CustomerViewSession = {
  view: string;
  since: number;
};

/**
 * How long the customer has been facing one screen, across page loads.
 *
 * The kiosk reloads itself while a screen is stuck — maintenance mode does it
 * every five minutes for its first three attempts — and an in-memory start time
 * restarts on each of those, so a duration held only in memory reports "time
 * since this page loaded" rather than "time this kiosk has been stuck". Any
 * alert with a threshold longer than the reload interval then never fires.
 *
 * Session storage is the right scope: it survives a reload, because a page
 * teardown runs no cleanup, and it ends with the browser session.
 */
function read(): CustomerViewSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const { view, since } = parsed as Partial<CustomerViewSession>;
    if (typeof view !== "string" || typeof since !== "number") return null;
    return { view, since };
  } catch {
    // Unavailable or corrupt storage degrades to a per-page-life duration
    // rather than failing the heartbeat that reports it.
    return null;
  }
}

function write(session: CustomerViewSession): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Safari privacy mode and quota exhaustion must not break reporting.
  }
}

/**
 * Begin, or continue, the session for `view`. A stored session for the same view
 * keeps its original start time, which is what makes a duration survive the
 * kiosk reloading itself. Any other view starts a new one.
 */
export function startCustomerViewSession(
  view: string,
  now: number,
): CustomerViewSession {
  const stored = read();
  if (stored?.view === view) return stored;
  const session = { view, since: now };
  write(session);
  return session;
}

export function readCustomerViewSession(): CustomerViewSession | null {
  return read();
}

export function clearCustomerViewSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Nothing to recover from; the next write replaces the value anyway.
  }
}
